import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';

/**
 * Module model of the web admin (issue #1670).
 *
 * A module is one grantable area of the product. The server holds the catalog
 * (`internal/authz/module.go`) and is the boundary: every route answers 403 to an
 * employee whose professional role holds none of the route's modules. The web
 * admin mirrors the catalog here to filter the sidebar, guard routes and gate
 * cross-module reads. That is UX only. `modules.test.ts` parses the Go file and
 * fails when this copy drifts.
 */
export type Module =
  | 'children'
  | 'families-contacts'
  | 'employees'
  | 'settling-in'
  | 'portfolio'
  | 'prospects'
  | 'capacity'
  | 'attendance-activities'
  | 'shifts-absences'
  | 'time-tracking-workforce'
  | 'messaging'
  | 'workflows'
  | 'event-calendar'
  | 'weekly-activities-menu'
  | 'kitchen'
  | 'courses'
  | 'documents'
  | 'contracts-tariffs'
  | 'billing'
  | 'mobile-employee'
  | 'self-service';

export type ModuleGroupKey = 'people' | 'daily-ops' | 'programs' | 'finance' | 'mobile';

export interface ModuleDefinition {
  key: Module;
  label: MessageDescriptor;
  description: MessageDescriptor;
}

export interface ModuleGroup {
  key: ModuleGroupKey;
  label: MessageDescriptor;
  modules: readonly ModuleDefinition[];
}

// Module labels reuse the sidebar `nav.*` ids so the Access roles page, the
// sidebar and the page titles share one translation per module.
export const MODULE_GROUPS: readonly ModuleGroup[] = [
  {
    key: 'people',
    // Explicit id with the same default as every other site, so the Access
    // roles page and the sidebar share one catalog entry and its translations.
    label: msg({ id: 'nav.sections.people', message: 'People' }),
    modules: [
      {
        key: 'children',
        label: msg({ id: 'nav.children', message: 'Children' }),
        description: msg({
          id: 'accessRoles.module.children.description',
          message: 'See and edit child records, health details and consents',
        }),
      },
      {
        key: 'families-contacts',
        label: msg({ id: 'nav.familiesContacts', message: 'Families & Contacts' }),
        description: msg({
          id: 'accessRoles.module.familiesContacts.description',
          message: 'Manage families, contacts, addresses and relationships',
        }),
      },
      {
        key: 'employees',
        label: msg({ id: 'nav.employees', message: 'Employees' }),
        description: msg({
          id: 'accessRoles.module.employees.description',
          message: 'Manage employee records, qualifications, work contracts and absence approvals',
        }),
      },
      {
        key: 'settling-in',
        label: msg({ id: 'nav.settlingIn', message: 'Settling-In' }),
        description: msg({
          id: 'accessRoles.module.settlingIn.description',
          message: 'Plan and track settling-in periods and sign them off',
        }),
      },
      {
        key: 'portfolio',
        label: msg({ id: 'nav.portfolio', message: 'Portfolio' }),
        description: msg({
          id: 'accessRoles.module.portfolio.description',
          message: 'Write and review portfolio entries for children',
        }),
      },
      {
        key: 'prospects',
        label: msg({ id: 'nav.prospects', message: 'Prospects' }),
        description: msg({
          id: 'accessRoles.module.prospects.description',
          message: 'Handle enquiries from new families: list, board and key figures',
        }),
      },
    ],
  },
  {
    key: 'daily-ops',
    label: msg({ id: 'nav.sections.dailyOperations', message: 'Daily Operations' }),
    modules: [
      {
        key: 'capacity',
        label: msg({ id: 'capacity.page.title', message: 'Capacity' }),
        description: msg({
          id: 'accessRoles.module.capacity.description',
          message: 'Plan places, allocations, group moves and child absences',
        }),
      },
      {
        key: 'attendance-activities',
        label: msg({ id: 'nav.attendanceActivities', message: 'Attendance & Activities' }),
        description: msg({
          id: 'accessRoles.module.attendanceActivities.description',
          message: 'Record attendance and daily activities',
        }),
      },
      {
        key: 'shifts-absences',
        label: msg({ id: 'nav.shiftAbsences', message: 'Shifts & Absences' }),
        description: msg({
          id: 'accessRoles.module.shiftsAbsences.description',
          message: 'Plan shifts, publish the schedule, approve absences',
        }),
      },
      {
        key: 'time-tracking-workforce',
        label: msg({ id: 'nav.timeTrackingWorkforce', message: 'Time Tracking & Workforce' }),
        description: msg({
          id: 'accessRoles.module.timeTrackingWorkforce.description',
          message: 'Review and approve time entries, overtime and time accounts',
        }),
      },
      {
        key: 'messaging',
        label: msg({ id: 'nav.messaging', message: 'Messages' }),
        description: msg({
          id: 'accessRoles.module.messaging.description',
          message: 'Read and send messages and broadcasts',
        }),
      },
      {
        key: 'workflows',
        label: msg({ id: 'nav.workflows', message: 'Approvals' }),
        description: msg({
          id: 'accessRoles.module.workflows.description',
          message: 'Handle workflow items, change requests and termination reviews',
        }),
      },
    ],
  },
  {
    key: 'programs',
    label: msg({ id: 'nav.sections.programsContent', message: 'Programs & Content' }),
    modules: [
      {
        key: 'event-calendar',
        label: msg({ id: 'nav.eventCalendar', message: 'Calendar' }),
        description: msg({
          id: 'accessRoles.module.eventCalendar.description',
          message: 'Plan and edit events in the calendar',
        }),
      },
      {
        key: 'weekly-activities-menu',
        label: msg({ id: 'nav.menuActivities', message: 'Weekly Activities & Menu' }),
        description: msg({
          id: 'accessRoles.module.weeklyActivitiesMenu.description',
          message: 'Plan the weekly activities and the menu',
        }),
      },
      {
        key: 'kitchen',
        label: msg({ id: 'nav.kitchen', message: 'Kitchen' }),
        description: msg({
          id: 'accessRoles.module.kitchen.description',
          message: 'See the kitchen view of meals and diets',
        }),
      },
      {
        key: 'courses',
        label: msg({ id: 'nav.courses', message: 'Courses' }),
        description: msg({
          id: 'accessRoles.module.courses.description',
          message: 'Manage courses, enrollments and course attendance',
        }),
      },
      {
        key: 'documents',
        label: msg({ id: 'nav.documents', message: 'Documents' }),
        description: msg({
          id: 'accessRoles.module.documents.description',
          message: 'Upload, publish and manage documents',
        }),
      },
    ],
  },
  {
    key: 'finance',
    // Same explicit id and default as the sidebar's Finance section.
    label: msg({ id: 'nav.sections.finance', message: 'Finance' }),
    modules: [
      {
        key: 'contracts-tariffs',
        label: msg({ id: 'nav.contractsTariffs', message: 'Contracts & Tariffs' }),
        description: msg({
          id: 'accessRoles.module.contractsTariffs.description',
          message: 'Manage childcare contracts, tariffs and payers',
        }),
      },
      {
        key: 'billing',
        label: msg({ id: 'nav.billing', message: 'Billing' }),
        description: msg({
          id: 'accessRoles.module.billing.description',
          message: 'Create and send invoices, handle dunning and payments',
        }),
      },
    ],
  },
  {
    key: 'mobile',
    label: msg({ id: 'accessRoles.group.mobile', message: 'Mobile' }),
    modules: [
      {
        key: 'mobile-employee',
        label: msg({ id: 'accessRoles.module.mobileEmployee', message: 'Employee app' }),
        description: msg({
          id: 'accessRoles.module.mobileEmployee.description',
          message: 'Daily use on the phone: activities, children, messages, shifts, time tracking',
        }),
      },
      {
        key: 'self-service',
        label: msg({ id: 'accessRoles.module.selfService', message: 'Self-service' }),
        description: msg({
          id: 'accessRoles.module.selfService.description',
          message:
            'Own data only: profile, balances and absences, own shifts, clock in and out, notifications, published documents. No child data.',
        }),
      },
    ],
  },
];

/** Every module in catalog order. */
export const ALL_MODULES: readonly Module[] = MODULE_GROUPS.flatMap((group) =>
  group.modules.map((module) => module.key),
);

const MODULE_SET: ReadonlySet<string> = new Set(ALL_MODULES);

export function isModule(value: string): value is Module {
  return MODULE_SET.has(value);
}

const MODULE_DEFINITIONS: ReadonlyMap<string, ModuleDefinition> = new Map(
  MODULE_GROUPS.flatMap((group) => group.modules.map((module) => [module.key, module] as const)),
);

/** The local label and description of a module key, `undefined` for an unknown key. */
export function getModuleDefinition(key: string): ModuleDefinition | undefined {
  return MODULE_DEFINITIONS.get(key);
}

export type WebModule = Exclude<Module, 'mobile-employee' | 'self-service'>;

/**
 * The modules of the web admin, in sidebar order. Mirrors `authz.WebModules()`:
 * every module except the app-only modules.
 */
export const WEB_MODULES: readonly WebModule[] = ALL_MODULES.filter(
  (module): module is WebModule => module !== 'mobile-employee' && module !== 'self-service',
);

/** The landing path of each web module. */
export const MODULE_HOME_PATHS: Record<WebModule, string> = {
  children: '/children',
  'families-contacts': '/contacts',
  employees: '/employees',
  'settling-in': '/settling-in',
  portfolio: '/portfolio',
  prospects: '/prospects',
  capacity: '/capacity',
  'attendance-activities': '/activities',
  'shifts-absences': '/shifts',
  'time-tracking-workforce': '/time-tracking',
  messaging: '/messaging',
  workflows: '/workflows',
  'event-calendar': '/calendar',
  'weekly-activities-menu': '/menu-activities',
  kitchen: '/kitchen',
  courses: '/courses',
  documents: '/documents',
  'contracts-tariffs': '/contracts',
  billing: '/billing',
};

/**
 * The home path of the first web module (sidebar order) in `modules`, or `null`
 * when the set holds no web module.
 */
export function firstWebModulePath(modules: ReadonlySet<Module>): string | null {
  const first = WEB_MODULES.find((module) => modules.has(module));
  return first ? MODULE_HOME_PATHS[first] : null;
}

/**
 * A top-level route belongs to one module, to a set of modules (any one opens
 * it, as the server's OR-guards do), or to the admin only.
 */
export type RouteAccess = Module | readonly Module[] | 'admin';

/** The modules of a non-admin route rule; any one of them opens the route. */
export function routeModules(access: Exclude<RouteAccess, 'admin'>): readonly Module[] {
  return typeof access === 'string' ? [access] : access;
}

/** `/contract-terminations*` and `/contract-groups/:id/revoke*` (terminate and revoke). */
export const CONTRACT_TERMINATION_MODULES: readonly Module[] = ['workflows', 'contracts-tariffs'];

/**
 * The access rule of every top-level `<Route path>` of the app shell
 * (`routes/appRoutes.tsx`). The nested billing route `billing` is keyed
 * `/billing`. `appRoutes.test.tsx` fails when a route is missing here or a key
 * has no route.
 */
export const ROUTE_MODULES: Record<string, RouteAccess> = {
  '/': 'admin',
  '/dashboard/analytics': 'admin',
  '/enrolment': 'admin',
  '/inspection': 'admin',
  '/analytics': 'admin',
  '/admin/subsidy-offices': 'admin',
  // Every settings page: the nested settings routes and the rail carry no gate of their own.
  '/settings': 'admin',
  '/children': 'children',
  '/children/new': 'children',
  '/children/consents': 'admin',
  '/children/:id/edit': 'children',
  '/contacts': 'families-contacts',
  '/contacts/create': 'families-contacts',
  '/contacts/:id/edit': 'families-contacts',
  '/employees': 'employees',
  '/employees/create': 'employees',
  '/employees/:id/edit': 'employees',
  '/settling-in': 'settling-in',
  '/settling-in/:id': 'settling-in',
  '/portfolio': 'portfolio',
  '/prospects': 'prospects',
  '/prospects/new': 'prospects',
  '/prospects/board': 'prospects',
  '/prospects/kpis': 'prospects',
  '/prospects/:id': 'prospects',
  '/capacity': 'capacity',
  '/capacity/graduation': 'capacity',
  '/activities': 'attendance-activities',
  '/shifts': 'shifts-absences',
  '/shifts/coverage': 'shifts-absences',
  // Group presence proof (#2114): the server narrows a non-admin to the groups they lead.
  '/team/presence': 'shifts-absences',
  '/time-tracking': 'time-tracking-workforce',
  '/workforce': 'time-tracking-workforce',
  '/messaging': 'messaging',
  '/workflows': 'workflows',
  '/calendar': 'event-calendar',
  '/menu-activities': 'weekly-activities-menu',
  '/menus': 'weekly-activities-menu',
  '/weekly-activities': 'weekly-activities-menu',
  '/kitchen': 'kitchen',
  '/courses': 'courses',
  '/documents': 'documents',
  '/contracts': 'contracts-tariffs',
  '/contracts/:id/edit': 'contracts-tariffs',
  // The terminate routes' OR-set. The page reads the workflow item only with `workflows`.
  '/contract-terminations/:id/review': CONTRACT_TERMINATION_MODULES,
  '/billing': 'billing',
  // The platform guide (#2059): open to anyone who can use the web admin; each
  // topic is filtered by its own modules.
  '/guide': WEB_MODULES,
  // What's new (#2109): the release notes, for everyone who uses the web admin.
  '/whats-new': WEB_MODULES,
};

// Admitting-module sets of the cross-module reads. A page may load data that
// belongs to another module; the server admits the read for any module of the
// set. The hooks gate on the same set, so an employee never gets a 403 on load.

/** EMP_LIST: `GET /employees`, `/employees/count` (docs/guides/access-roles.md). */
export const EMPLOYEE_LIST_MODULES: readonly Module[] = [
  'employees',
  'shifts-absences',
  'time-tracking-workforce',
  'capacity',
  'messaging',
];

/**
 * `GET /children/:id/full` (`childFullReadModules` in `internal/handlers/children`):
 * CHILD_DETAIL plus the contract and billing pages, which show the child card (#1691).
 */
export const CHILD_DETAIL_READ_MODULES: readonly Module[] = [
  'children',
  'settling-in',
  'portfolio',
  'attendance-activities',
  'capacity',
  'mobile-employee',
  'contracts-tariffs',
  'billing',
];

/** `GET /employees/:id`. */
export const EMPLOYEE_DETAIL_READ_MODULES: readonly Module[] = ['employees'];

/**
 * `GET /children/:id/relationships` and `.../pickup` (REL_READ,
 * `relationshipReadModules` in `internal/handlers/relationships`).
 */
export const RELATIONSHIP_READ_MODULES: readonly Module[] = [
  'families-contacts',
  'children',
  'messaging',
  'mobile-employee',
];

/**
 * `GET /children/:id/relationships/pickup` (`pickupReadModules` in
 * `internal/handlers/relationships`): REL_READ plus the attendance check-out (#1691).
 */
export const PICKUP_CONTACT_READ_MODULES: readonly Module[] = [
  'families-contacts',
  'children',
  'messaging',
  'mobile-employee',
  'attendance-activities',
];

/** Settling-in plan writes, e.g. `POST|PUT /settling-in/:id/complete` (`planOwner`). */
export const SETTLING_IN_WRITE_MODULES: readonly Module[] = ['settling-in'];

/** `GET /settling-in` (plan list). */
export const SETTLING_IN_READ_MODULES: readonly Module[] = ['settling-in', 'mobile-employee'];

/** `GET /contracts` (the capacity page's allocation form reads a child's contracts, #1691). */
export const CONTRACT_READ_MODULES: readonly Module[] = ['contracts-tariffs', 'billing', 'capacity'];

/**
 * `GET /tariff-versions` (`listRead` in `internal/handlers/billing/tariff_handler.go`):
 * the capacity page resolves the contract's tariff version for the age transition (#1691).
 */
export const TARIFF_VERSION_LIST_READ_MODULES: readonly Module[] = ['contracts-tariffs', 'billing', 'capacity'];

/** `GET /allocations`. */
export const ALLOCATION_READ_MODULES: readonly Module[] = ['capacity'];

/** `GET /group-combinations` (#1688 D2: the shift planner of a shifts-absences holder reads them). */
export const GROUP_COMBINATION_READ_MODULES: readonly Module[] = ['capacity', 'shifts-absences'];

/** `GET /workflow-items/pending/count`. */
export const WORKFLOW_READ_MODULES: readonly Module[] = ['workflows'];

/**
 * `GET /organisations/:id/workflow-settings`: the workflows page, and the
 * termination intake that reads the org's notice period (#1691).
 */
export const WORKFLOW_SETTINGS_READ_MODULES: readonly Module[] = ['workflows', 'contracts-tariffs'];

/** `GET /dunning/workflows/:id` (the dunning drawer). */
export const DUNNING_READ_MODULES: readonly Module[] = ['billing'];

/**
 * The modules a workflow item's detail view needs, per category. The inbox rows
 * stay visible to every `workflows` holder (the counts stay honest); only the
 * detail of a listed category is gated. A category not listed opens the shared
 * workflow detail sheet, which `workflows` already admits.
 */
export const WORKFLOW_CATEGORY_DETAIL_MODULES: Readonly<Partial<Record<string, readonly Module[]>>> = {
  dunning: DUNNING_READ_MODULES,
};

/** `GET /messages/unread-count`. */
export const MESSAGE_UNREAD_READ_MODULES: readonly Module[] = ['messaging', 'mobile-employee'];
